import { useTranslation } from 'react-i18next';
import { MoreVertical, Pencil, Trash2, LayoutTemplate } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import type { TemplateGalleryItem } from '../types';

interface TemplateCardProps {
  template: TemplateGalleryItem;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function TemplateCard({
  template,
  selected,
  onSelect,
  onEdit,
  onDelete,
}: TemplateCardProps) {
  const { t } = useTranslation('projectTemplates');
  const isTenantOwned = !!template.tenantId;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50',
      )}
      data-testid={`project-templates-card-${template.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
          <LayoutTemplate className="size-4" />
        </div>

        {isTenantOwned && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('gallery.card.edit')}
                    data-testid={`project-templates-card-menu-${template.id}`}
                  >
                    <MoreVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('gallery.card.edit')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                onClick={onEdit}
                data-testid={`project-templates-card-edit-${template.id}`}
              >
                <Pencil className="mr-2 size-4" />
                {t('gallery.card.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={onDelete}
                data-testid={`project-templates-card-delete-${template.id}`}
              >
                <Trash2 className="mr-2 size-4" />
                {t('gallery.card.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <p className="text-sm font-medium leading-tight">{template.title}</p>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="font-normal">
          {t(`gallery.sectorFilter.${template.sector}`)}
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground">
        {t('gallery.card.taskCount', { count: template.taskCount })} ·{' '}
        {t('gallery.card.statusCount', { count: template.statusCount })}
      </p>
    </div>
  );
}
